import { useState, useMemo, useEffect } from "react";
import { Search, Settings, X } from "lucide-react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BottomNavigation } from "@/components/BottomNavigation";
import { PageTransition } from "@/components/PageTransition";
import { ProductCard } from "@/components/ProductCard";
import { ProductDetail } from "@/components/ProductDetail";
import { ProductAdminPanel } from "@/components/ProductAdminPanel";
import { MenuDrawer } from "@/components/MenuDrawer";
import { useProducts, useProductCategories, type Product } from "@/hooks/useProducts";
import { useAdminRole } from "@/hooks/useAdminRole";
import { supabase } from "@/integrations/supabase/client";

const Prodotti = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const { products, loading, refetch } = useProducts();
  const { categories, refetch: refetchCategories } = useProductCategories();
  const { isAdmin } = useAdminRole(userId);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserId(data.user?.id || null);
    });
  }, []);

  // Multilingual search keywords mapping
  const searchKeywords: Record<string, string[]> = {
    iphone: ["iphone", "telefono", "phone", "cellulare", "smartphone", "móvil", "téléphone", "handy"],
    mac: ["mac", "macbook", "computer", "laptop", "portatile", "ordinateur", "computadora"],
    ipad: ["ipad", "tablet", "tablette", "tavoletta"],
    airpods: ["airpods", "cuffie", "auricolari", "headphones", "earphones", "écouteurs", "kopfhörer"],
    watch: ["watch", "orologio", "montre", "reloj", "uhr", "smartwatch"],
  };

  const filteredProducts = useMemo(() => {
    let filtered = products.filter(p => p.is_active);

    if (selectedCategory) {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => {
        // Direct name/description match
        if (product.name.toLowerCase().includes(query)) return true;
        if (product.description?.toLowerCase().includes(query)) return true;
        if (product.category?.name.toLowerCase().includes(query)) return true;

        // Multilingual keyword matching
        for (const [, keywords] of Object.entries(searchKeywords)) {
          if (keywords.some(kw => kw.includes(query) || query.includes(kw))) {
            const categorySlug = product.category?.slug || "";
            if (keywords.some(kw => 
              product.name.toLowerCase().includes(kw) || 
              categorySlug.includes(kw)
            )) {
              return true;
            }
          }
        }

        return false;
      });
    }

    return filtered;
  }, [products, selectedCategory, searchQuery]);


  if (selectedProduct) {
    return (
      <ProductDetail 
        product={selectedProduct} 
        onBack={() => setSelectedProduct(null)} 
      />
    );
  }

  return (
    <PageTransition>
      <div className="min-h-screen bg-background pb-24">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-foreground">Prodotti</h1>
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowAdminPanel(!showAdminPanel)}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {showAdminPanel ? "Catalogo" : "Gestione"}
                </Button>
              )}
            </div>

            {!showAdminPanel && (
              <>
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Cerca prodotti..."
                    className="pl-10 h-12 bg-muted/50"
                  />
                  {searchQuery && (
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setSearchQuery("")}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Category Filters */}
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
                    <Button
                      variant={selectedCategory === null ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        setSelectedCategory(null);
                      }}
                      className="flex-shrink-0"
                    >
                      Tutti
                    </Button>
                {categories.map(category => {
                      const isSelected = selectedCategory === category.id;

                      return (
                        <Button
                          key={category.id}
                          variant={isSelected ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            setSelectedCategory(category.id);
                          }}
                          className="flex-shrink-0"
                        >
                          {category.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-6">
          {showAdminPanel ? (
            <ProductAdminPanel 
              products={products}
              categories={categories}
              onRefresh={refetch}
              onRefreshCategories={refetchCategories}
            />
          ) : (
            <>
              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="aspect-square bg-muted animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-muted-foreground text-lg">Nessun prodotto trovato</p>
                  {searchQuery && (
                    <Button 
                      variant="link" 
                      onClick={() => setSearchQuery("")}
                      className="mt-2"
                    >
                      Cancella ricerca
                    </Button>
                  )}
                </div>
              ) : (
                <motion.div 
                  className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ staggerChildren: 0.05 }}
                >
                  {filteredProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <ProductCard 
                        product={product}
                        onClick={() => setSelectedProduct(product)}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </>
          )}
        </main>

        {/* Menu Drawer */}
        <MenuDrawer open={menuOpen} onOpenChange={setMenuOpen} />

        <BottomNavigation onMenuClick={() => setMenuOpen(true)} activeTab="prodotti" />
      </div>
    </PageTransition>
  );
};

export default Prodotti;
